import { useEffect, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ApiError, api } from "../../services/apiClient";
import { apiErrorToMessage } from "../../services/apiError";
import { getConfig } from "../../config";
import { ADMIN_TABS, adminTabFromPath, type AdminTab } from "./adminGate";
import { useAdminSession } from "./AdminSessionGate";
import { AdminSettings } from "./AdminSettings";
import { RegisteredUsers } from "./RegisteredUsers";
import { WahaSettings } from "./WahaSettings";
import {
  summarizeOfficers, summarizeRecordings, recentRecordings, hasReportedMetrics, nonZeroKpis,
  formatKpiValue, KPI_UNITS
} from "./dashboardSummary";
import { useI18n } from "../../i18n/localization";

interface OfficerRow { officerId: number; displayName: string; unit?: string; available: boolean; }
interface RecordingRow {
  recordingId: number;
  callId: number;
  fileName?: string;
  status: string;
  sizeBytes?: number;
  durationSec?: number;
  createdAt: string;
  completedAt?: string;
  signedAt?: string;
  systemCode?: string;
  driverId?: string;
  caseRef?: string;
}
interface RecordingDetail {
  recording: RecordingRow;
  fileAvailable: boolean;
  manifestAvailable: boolean;
  signatureAvailable: boolean;
}
interface Dashboard { kpis: { name: string; value: number }[]; serviceHealth: Record<string, boolean>; }
interface RecordingViewLoginResponse { token: string; expiresAt: string; }

const RECORDING_VIEW_TOKEN_KEY = "vc.recordingViewToken";

function readRecordingViewToken(): string {
  try {
    return window.sessionStorage.getItem(RECORDING_VIEW_TOKEN_KEY) ?? "";
  } catch {
    return "";
  }
}

function rememberRecordingViewToken(token: string): string {
  try {
    window.sessionStorage.setItem(RECORDING_VIEW_TOKEN_KEY, token);
  } catch {
    // Hardened embedded browsers can disable storage; the token remains in component state.
  }
  return token;
}

function forgetRecordingViewToken() {
  try {
    window.sessionStorage.removeItem(RECORDING_VIEW_TOKEN_KEY);
  } catch {
    // ignore
  }
}

// Build an absolute URL to a recording file endpoint carrying the ADMIN session token as a
// query param — a <video> element / download anchor cannot send an Authorization header.
// Only playback receives the extra short-lived recording-view token.
function fileUrl(recordingId: number, action: "playback" | "download", token: string, recordingViewToken = ""): string {
  const base = getConfig().apiBaseUrl;
  const params = new URLSearchParams({ access_token: token });
  if (action === "playback" && recordingViewToken) {
    params.set("recording_access_token", recordingViewToken);
  }
  return `${base}/admin/recordings/${recordingId}/${action}?${params.toString()}`;
}

function formatSize(bytes?: number): string {
  if (bytes == null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatWhen(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getUTCDate())}/${p(d.getUTCMonth() + 1)} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
}

// Admin console POC (design/05 §6, modules/01 §10). Rendered only inside <AdminSessionGate>
// (administrator sign-in); every /admin/* endpoint is also session-gated server-side.
// The active tab is part of the URL (/admin, /admin/officers, ...) so it can be linked to.
export function AdminConsole() {
  const { t, dir } = useI18n();
  const { session } = useAdminSession();
  const adminToken = session.token;
  const navigate = useNavigate();
  const tab = adminTabFromPath(useLocation().pathname);
  const setTab = (next: AdminTab) => navigate(next === "dashboard" ? "/admin" : `/admin/${next}`);
  const tabLabel: Record<AdminTab, string> = {
    dashboard: t.admin.dashboard, officers: t.admin.officers, recordings: t.admin.recordings, settings: t.admin.settings
  };

  return (
    <main
      dir={dir}
      style={{
        // Use the available desktop width like an operations console (not a narrow centered
        // form), while staying padded and responsive down to mobile.
        width: "min(calc(100vw - 32px), 1200px)", margin: "0 auto", padding: "20px 24px",
        boxSizing: "border-box", fontFamily: "Segoe UI, Arial, sans-serif"
      }}
    >
      <h1 style={{ color: "#14305f", fontSize: 19 }}>{t.admin.title}</h1>
      <nav aria-label={t.admin.title} style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        {ADMIN_TABS.map((id) => (
          <TabBtn key={id} active={tab === id} onClick={() => setTab(id)}>{tabLabel[id]}</TabBtn>
        ))}
      </nav>
      {tab === "dashboard" && <DashboardView token={adminToken} />}
      {tab === "officers" && <OfficersView token={adminToken} />}
      {tab === "recordings" && <RecordingsView token={adminToken} />}
      {tab === "settings" && (
        <>
          <RegisteredUsers />
          <WahaSettings />
          <AdminSettings />
        </>
      )}
    </main>
  );
}

// Operational dashboard — composes /admin/dashboard (health + raw KPIs), /admin/officers, and
// /admin/recordings into structured sections built from REAL data. Raw vc_* metrics are demoted
// to a secondary monitoring section that shows a clear note when nothing has reported yet.
function DashboardView({ token }: { token: string }) {
  const { t } = useI18n();
  const { guard } = useAdminSession();
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [officers, setOfficers] = useState<OfficerRow[] | null>(null);
  const [recordings, setRecordings] = useState<RecordingRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setError(null);
    guard(Promise.all([
      api.get<Dashboard>("/admin/dashboard", token),
      api.get<OfficerRow[]>("/admin/officers", token),
      api.get<RecordingRow[]>("/admin/recordings?limit=100", token)
    ]))
      .then(([d, o, r]) => { setDashboard(d); setOfficers(o); setRecordings(r); })
      .catch((e: unknown) => setError(apiErrorToMessage(e, t)));
  };

  useEffect(() => { load(); /* refetch on token change */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (error) return <Err msg={error} />;
  if (!dashboard || !officers || !recordings) return <p style={{ color: "#5b6b82", fontSize: 14 }}>{t.rec.loading}</p>;

  const off = summarizeOfficers(officers);
  const rec = summarizeRecordings(recordings);
  const metricsReported = hasReportedMetrics(dashboard.kpis);
  const recent = recentRecordings(recordings, 6);

  const kpiText = (name: string, value: number): string => {
    const unit = KPI_UNITS[name] ?? "count";
    const v = formatKpiValue(unit, value);
    return unit === "seconds" ? `${v} ${t.dash.secondsUnit}` : v;
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <span style={{ color: "#64748b", fontSize: 13 }}>{t.dash.subtitle}</span>
        <button onClick={load} style={{ background: "#e2e8f0", color: "#1e2a3a", border: "none", borderRadius: 6, padding: "6px 14px", cursor: "pointer", fontSize: 13 }}>
          {t.dash.refresh}
        </button>
      </div>

      {/* Operational status — headline tiles from real officer + recording data. */}
      <Section title={t.dash.sectionStatus}>
        <div style={tileRow}>
          <StatTile label={t.dash.officersAvailable} value={String(off.available)} sub={`${t.dash.of} ${off.total}`} tone={off.available > 0 ? "ok" : "muted"} />
          <StatTile label={t.dash.recInProgress} value={String(rec.inProgress)} tone={rec.inProgress > 0 ? "warn" : "muted"} />
          <StatTile label={t.dash.recCompleted} value={String(rec.completed)} tone="ok" />
          <StatTile label={t.dash.storageUsed} value={formatSize(rec.totalBytes)} tone="neutral" />
        </div>
      </Section>

      {/* Service health + officer availability sit balanced side-by-side on desktop, stack on narrow. */}
      <div style={twoColGrid}>
        <Section title={t.dash.sectionHealth}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {Object.entries(dashboard.serviceHealth).map(([svc, up]) => (
              <span key={svc} style={healthChip(up)}>
                <span style={{ width: 8, height: 8, borderRadius: 999, background: up ? "#15803d" : "#b91c1c", display: "inline-block" }} />
                {t.dash.services[svc] ?? svc} — {up ? t.dash.serviceUp : t.dash.serviceDown}
              </span>
            ))}
          </div>
        </Section>

        <Section title={t.dash.sectionOfficers}>
          <AvailabilityBar available={off.available} total={off.total} />
          <div style={{ color: "#64748b", fontSize: 12, marginTop: 6 }}>
            {t.dash.officersAvailable}: {off.available} · {t.dash.officersUnavailable}: {off.unavailable} · {t.dash.of} {off.total}
          </div>
        </Section>
      </div>

      {/* Recordings status mix — full section width. */}
      <Section title={t.dash.sectionRecordings}>
        <div style={tileRow}>
          <MiniStat label={t.dash.recCompleted} value={String(rec.completed)} />
          <MiniStat label={t.dash.recInProgress} value={String(rec.inProgress)} />
          <MiniStat label={t.dash.recQueued} value={String(rec.statusCounts["queued"] ?? 0)} />
          <MiniStat label={t.dash.recFailed} value={String(rec.failed)} warn={rec.failed > 0} />
          <MiniStat label={t.dash.recSigned} value={String(rec.signed)} />
          <MiniStat label={t.dash.totalMinutes} value={String(rec.totalMinutes)} />
        </div>
        <div style={{ color: "#64748b", fontSize: 12, marginTop: 8 }}>
          {t.dash.latestRecording}: <Ltr>{formatWhen(rec.latestCreatedAt)}</Ltr>
        </div>
      </Section>

      {/* Recent activity — latest recordings, full section width. */}
      <Section title={t.dash.sectionActivity}>
        {recent.length === 0 ? (
          <p style={{ color: "#5b6b82", fontSize: 13, margin: 0 }}>{t.dash.noActivity}</p>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: "start", color: "#64748b" }}>
                <th style={{ padding: 6 }}>{t.rec.colStatus}</th>
                <th style={{ padding: 6 }}>{t.rec.colCase}</th>
                <th style={{ padding: 6 }}>{t.rec.colSize}</th>
                <th style={{ padding: 6 }}>{t.rec.colCreated}</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((r) => (
                <tr key={r.recordingId} style={{ borderBottom: "1px solid #eef3fb" }}>
                  <td style={{ padding: 6 }}>{r.status}</td>
                  <td style={{ padding: 6, color: "#64748b" }}><Ltr>{[r.systemCode, r.caseRef, r.driverId].filter(Boolean).join(" / ") || "—"}</Ltr></td>
                  <td style={{ padding: 6, color: "#64748b" }}><Ltr>{formatSize(r.sizeBytes)}</Ltr></td>
                  <td style={{ padding: 6, color: "#64748b" }}><Ltr>{formatWhen(r.createdAt)}</Ltr></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      {/* Secondary monitoring feed — clear note when infra metrics have not reported yet. */}
      <Section title={t.dash.sectionMetrics}>
        {!metricsReported ? (
          <p style={{ color: "#b45309", fontSize: 13, margin: 0 }}>{t.dash.metricsNotReported}</p>
        ) : (
          <div style={tileRow}>
            {nonZeroKpis(dashboard.kpis).map((k) => (
              <MiniStat key={k.name} label={t.dash.kpi[k.name] ?? k.name} value={kpiText(k.name, k.value)} />
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

// Isolate a value with Latin units/digits so it renders left-to-right inside the RTL layout
// (e.g. "384.6 MB" instead of the bidi-reordered "MB 384.6"), without affecting alignment.
function Ltr({ children }: { children: ReactNode }) {
  return <span dir="ltr" style={{ unicodeBidi: "isolate" }}>{children}</span>;
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: 14, background: "#fff" }}>
      <h2 style={{ fontSize: 14, color: "#14305f", margin: "0 0 10px", fontWeight: 600 }}>{title}</h2>
      {children}
    </section>
  );
}

const tileRow: React.CSSProperties = { display: "flex", flexWrap: "wrap", gap: 10 };
// Balanced two-up on desktop, single column when narrow — no media queries needed.
const twoColGrid: React.CSSProperties = {
  display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 16, alignItems: "start"
};

function StatTile({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone: "ok" | "warn" | "muted" | "neutral" }) {
  const color = tone === "ok" ? "#15803d" : tone === "warn" ? "#b45309" : tone === "muted" ? "#64748b" : "#14305f";
  return (
    <div style={{ flex: "1 1 150px", minWidth: 140, border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 14px", background: "#f8fafc" }}>
      <div style={{ fontSize: 12, color: "#64748b" }}>{label}</div>
      {/* dir=ltr keeps mixed numeric/unit values (e.g. "384.6 MB") in natural order. */}
      <div dir="ltr" style={{ fontSize: 26, fontWeight: 600, color, textAlign: "right" }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "#94a3b8" }}>{sub}</div>}
    </div>
  );
}

function MiniStat({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <div style={{ flex: "1 1 110px", minWidth: 100, border: "1px solid #eef3fb", borderRadius: 6, padding: "8px 12px" }}>
      <div style={{ fontSize: 11, color: "#64748b" }}>{label}</div>
      <div dir="ltr" style={{ fontSize: 18, color: warn ? "#b91c1c" : "#14305f", textAlign: "right" }}>{value}</div>
    </div>
  );
}

function AvailabilityBar({ available, total }: { available: number; total: number }) {
  const pct = total > 0 ? Math.round((available / total) * 100) : 0;
  return (
    <div style={{ background: "#eef3fb", borderRadius: 999, height: 12, overflow: "hidden" }}>
      <div style={{ width: `${pct}%`, height: "100%", background: "#15803d" }} />
    </div>
  );
}

function healthChip(up: boolean): React.CSSProperties {
  return {
    display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13,
    color: up ? "#14532d" : "#7f1d1d", background: up ? "#dcfce7" : "#fee2e2",
    border: `1px solid ${up ? "#bbf7d0" : "#fecaca"}`, borderRadius: 999, padding: "4px 12px"
  };
}

function OfficersView({ token }: { token: string }) {
  const { t } = useI18n();
  const { guard } = useAdminSession();
  const [rows, setRows] = useState<OfficerRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    guard(api.get<OfficerRow[]>("/admin/officers", token)).then(setRows).catch((e: unknown) => setError(apiErrorToMessage(e, t)));
  }, [token, t, guard]);
  if (error) return <Err msg={error} />;
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
      <tbody>
        {rows.map((o) => (
          <tr key={o.officerId} style={{ borderBottom: "1px solid #eef3fb" }}>
            <td style={{ padding: 8 }}>{o.displayName}</td>
            <td style={{ padding: 8, color: "#64748b" }}>{o.unit}</td>
            <td style={{ padding: 8, color: o.available ? "#15803d" : "#64748b" }}>
              {o.available ? t.availability.available : t.availability.unavailable}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function RecordingsView({ token }: { token: string }) {
  return <RecordingsWorkspace token={token} />;
}

function RecordingPasswordGate({ token, onAuthenticated }: { token: string; onAuthenticated: (token: string) => void }) {
  const { t } = useI18n();
  const { guard } = useAdminSession();
  const [user, setUser] = useState("");
  const [pass, setPass] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await guard(api.post<RecordingViewLoginResponse>("/admin/recording-view/login", { user, pass }, token));
      onAuthenticated(res.token);
    } catch (err) {
      setError(err instanceof ApiError && err.status === 401 ? t.rec.authFailed : apiErrorToMessage(err, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} style={{ border: "1px solid #d6deea", borderRadius: 10, background: "#fff", padding: 18, maxWidth: 420 }}>
      <h2 style={{ color: "#14305f", fontSize: 16, margin: "0 0 8px" }}>{t.rec.authTitle}</h2>
      <p style={{ color: "#64748b", fontSize: 13, margin: "0 0 14px" }}>{t.rec.authHint}</p>
      <label style={authLabel}>
        {t.rec.username}
        <input value={user} onChange={(e) => setUser(e.target.value)} autoComplete="username"
          style={authInput} autoFocus />
      </label>
      <label style={authLabel}>
        {t.rec.password}
        <input value={pass} onChange={(e) => setPass(e.target.value)} type="password" autoComplete="current-password"
          style={authInput} />
      </label>
      {error && <div style={{ color: "#b91c1c", fontSize: 13, marginBottom: 10 }}>{error}</div>}
      <button disabled={busy} type="submit"
        style={{ background: busy ? "#94a3b8" : "#1d4ed8", color: "#fff", border: "none", borderRadius: 6, padding: "8px 18px", cursor: busy ? "default" : "pointer" }}>
        {busy ? t.rec.authChecking : t.rec.authSubmit}
      </button>
    </form>
  );
}

const authLabel: React.CSSProperties = { display: "block", color: "#14305f", fontSize: 13, marginBottom: 10 };
const authInput: React.CSSProperties = {
  display: "block", width: "100%", marginTop: 4, padding: "8px 10px",
  border: "1px solid #d6deea", borderRadius: 6, boxSizing: "border-box"
};

function RecordingsWorkspace({ token }: {
  token: string;
}) {
  const { t } = useI18n();
  const { guard } = useAdminSession();
  const [rows, setRows] = useState<RecordingRow[]>([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const load = () => {
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    if (status) params.set("status", status);
    params.set("limit", "100");
    guard(api.get<RecordingRow[]>(`/admin/recordings?${params.toString()}`, token))
      .then((r) => { setRows(r); setError(null); })
      .catch((e: unknown) => setError(apiErrorToMessage(e, t)));
  };

  useEffect(() => {
    void load();
    // initial load only; subsequent loads via the search controls
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") load(); }}
          placeholder={t.rec.search}
          style={{ flex: 1, minWidth: 220, padding: "6px 10px", border: "1px solid #d6deea", borderRadius: 6 }}
        />
        <select value={status} onChange={(e) => setStatus(e.target.value)}
          style={{ padding: "6px 10px", border: "1px solid #d6deea", borderRadius: 6 }}>
          <option value="">{t.rec.statusAll}</option>
          <option value="completed">{t.rec.statusCompleted}</option>
          <option value="queued">{t.rec.statusQueued}</option>
          <option value="compositing">{t.rec.statusCompositing}</option>
          <option value="capturing">{t.rec.statusCapturing}</option>
          <option value="failed">{t.rec.statusFailed}</option>
        </select>
        <button onClick={load} style={{ background: "#1d4ed8", color: "#fff", border: "none", borderRadius: 6, padding: "6px 16px", cursor: "pointer" }}>
          {t.rec.refresh}
        </button>
      </div>

      {error && <Err msg={error} />}
      {rows.length === 0 && !error && <p style={{ color: "#5b6b82", fontSize: 14 }}>{t.rec.none}</p>}

      {/* Two-column workspace: scrollable list + a detail column that stays in view, so
          selecting a row surfaces the player immediately. Columns wrap (stack) on narrow
          screens via flexWrap + per-column min widths. */}
      {rows.length > 0 && (
        <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 360px", minWidth: 300, maxHeight: 540, overflow: "auto", border: "1px solid #eef3fb", borderRadius: 8 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: "start", color: "#64748b", position: "sticky", top: 0, background: "#fff" }}>
                  <th style={{ padding: 6 }}>{t.rec.colStatus}</th>
                  <th style={{ padding: 6 }}>{t.rec.colCall}</th>
                  <th style={{ padding: 6 }}>{t.rec.colCase}</th>
                  <th style={{ padding: 6 }}>{t.rec.colSize}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.recordingId}
                    onClick={() => setSelectedId(r.recordingId)}
                    style={{
                      borderBottom: "1px solid #eef3fb", cursor: "pointer",
                      background: selectedId === r.recordingId ? "#dbe7ff" : undefined
                    }}
                  >
                    <td style={{ padding: 6 }}>{r.status}</td>
                    <td style={{ padding: 6, color: "#64748b" }}>#{r.callId}</td>
                    <td style={{ padding: 6, color: "#64748b" }}>
                      <Ltr>{[r.systemCode, r.caseRef, r.driverId].filter(Boolean).join(" / ") || "—"}</Ltr>
                    </td>
                    <td style={{ padding: 6, color: "#64748b" }}><Ltr>{formatSize(r.sizeBytes)}</Ltr></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ flex: "1 1 420px", minWidth: 320, position: "sticky", top: 12 }}>
            {selectedId != null ? (
              <RecordingDetailPanel recordingId={selectedId} token={token} onClose={() => setSelectedId(null)} />
            ) : (
              <div style={{ border: "1px dashed #d6deea", borderRadius: 8, padding: 24, color: "#5b6b82", fontSize: 14, textAlign: "center" }}>
                {t.rec.selectHint}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function RecordingDetailPanel({ recordingId, token, onClose }: {
  recordingId: number;
  token: string;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const { guard } = useAdminSession();
  const [detail, setDetail] = useState<RecordingDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [manifest, setManifest] = useState<string | null>(null);
  const [signature, setSignature] = useState<string | null>(null);
  const [recordingViewToken, setRecordingViewToken] = useState(() =>
    typeof window === "undefined" ? "" : readRecordingViewToken()
  );
  const [playbackError, setPlaybackError] = useState<string | null>(null);

  useEffect(() => {
    setDetail(null); setManifest(null); setSignature(null); setError(null);
    guard(api.get<RecordingDetail>(`/admin/recordings/${recordingId}`, token))
      .then(setDetail)
      .catch((e: unknown) => setError(apiErrorToMessage(e, t)));
  }, [recordingId, token, t, guard]);

  const showManifest = () =>
    guard(api.get<unknown>(`/admin/recordings/${recordingId}/manifest`, token))
      .then((m) => setManifest(JSON.stringify(m, null, 2)))
      .catch((e: unknown) => setManifest(apiErrorToMessage(e, t)));

  const showSignature = () =>
    guard(api.get<unknown>(`/admin/recordings/${recordingId}/signature`, token))
      .then((s) => setSignature(JSON.stringify(s, null, 2)))
      .catch((e: unknown) => setSignature(apiErrorToMessage(e, t)));

  return (
    <div style={{ border: "1px solid #d6deea", borderRadius: 8, padding: 14, background: "#fff" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <strong style={{ color: "#14305f" }}>{t.rec.details} #{recordingId}</strong>
        <button onClick={onClose} style={{ background: "#e2e8f0", color: "#1e2a3a", border: "none", borderRadius: 6, padding: "4px 12px", cursor: "pointer" }}>
          {t.rec.close}
        </button>
      </div>

      {error && <Err msg={error} />}
      {!detail && !error && <p style={{ color: "#5b6b82", fontSize: 13 }}>{t.rec.loading}</p>}
      {detail && (
        <div>
          <div style={{ fontSize: 12, color: "#64748b", marginBottom: 10 }}>
            {t.rec.colStatus}: {detail.recording.status} · {t.rec.colCreated}: <Ltr>{formatWhen(detail.recording.createdAt)}</Ltr> · {t.rec.colDuration}:{" "}
            {detail.recording.durationSec != null ? `${detail.recording.durationSec} ${t.rec.seconds}` : "—"} · {t.rec.colSize}: <Ltr>{formatSize(detail.recording.sizeBytes)}</Ltr>
          </div>
          {detail.fileAvailable ? (
            <>
              {playbackError && <div style={{ marginBottom: 10 }}><Err msg={playbackError} /></div>}
              {recordingViewToken ? (
                <video
                  controls
                  src={fileUrl(recordingId, "playback", token, recordingViewToken)}
                  onError={() => {
                    forgetRecordingViewToken();
                    setRecordingViewToken("");
                    setPlaybackError(t.rec.authExpired);
                  }}
                  style={{ width: "100%", maxHeight: 360, background: "#000", borderRadius: 6 }}
                />
              ) : (
                <RecordingPasswordGate
                  token={token}
                  onAuthenticated={(t) => {
                    setPlaybackError(null);
                    setRecordingViewToken(rememberRecordingViewToken(t));
                  }}
                />
              )}
              <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                <a
                  href={fileUrl(recordingId, "download", token)}
                  style={{ background: "#15803d", color: "#fff", borderRadius: 6, padding: "6px 14px", textDecoration: "none" }}
                >
                  {t.rec.download}
                </a>
                {detail.manifestAvailable && (
                  <button onClick={showManifest} style={detailBtn}>{t.rec.manifest}</button>
                )}
                {detail.signatureAvailable && (
                  <button onClick={showSignature} style={detailBtn}>{t.rec.signature}</button>
                )}
              </div>
            </>
          ) : (
            <p style={{ color: "#b45309", fontSize: 13 }}>{t.rec.notPlayable}</p>
          )}

          {manifest && <Sidecar title={t.rec.manifest} body={manifest} />}
          {signature && <Sidecar title={t.rec.signature} body={signature} />}
        </div>
      )}
    </div>
  );
}

function Sidecar({ title, body }: { title: string; body: string }) {
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}>{title}</div>
      <pre dir="ltr" style={{ background: "#0f1e3a", color: "#dbe7ff", padding: 12, borderRadius: 6, fontSize: 12, overflow: "auto", maxHeight: 240, margin: 0 }}>
        {body}
      </pre>
    </div>
  );
}

const detailBtn: React.CSSProperties = {
  background: "#1d4ed8", color: "#fff", border: "none", borderRadius: 6, padding: "6px 14px", cursor: "pointer"
};

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      style={{
        background: active ? "#1d4ed8" : "#e2e8f0", color: active ? "#fff" : "#1e2a3a",
        border: "none", borderRadius: 6, padding: "6px 16px", cursor: "pointer"
      }}
    >
      {children}
    </button>
  );
}

function Err({ msg }: { msg: string }) {
  return <div style={{ color: "#b91c1c", fontSize: 13 }}>{msg}</div>;
}
